import type {
    AddCommerceEnrollmentProductRequest,
    BeginCommerceEnrollmentRequest,
    CommerceEnrollmentChangedEvent,
    CommerceEnrollmentChangedListener,
    CommerceEnrollmentDomain,
    CommerceEnrollmentSession,
    CommerceEventUnsubscribe,
    CommerceOperationOptions,
    CommerceCart,
} from "@teqtank/commerce-contracts";
import { CommerceError } from "../errors/commerce-error.js";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceEnrollmentRepository } from "./commerce-enrollment-repository.js";

export class CommerceEnrollmentService implements CommerceEnrollmentDomain {
    private readonly listeners = new DisposableListenerRegistry<CommerceEnrollmentChangedEvent>();
    private value: CommerceEnrollmentSession | null = null;
    private restoreInFlight: Promise<CommerceEnrollmentSession | null> | undefined;
    constructor(
        private readonly repository: CommerceEnrollmentRepository,
        private readonly assertAvailable: () => void,
        private readonly synchronizeCart: (
            enrollment: CommerceEnrollmentSession | null,
        ) => Promise<void>,
    ) {}
    get current() {
        return this.value;
    }
    async begin(
        request: BeginCommerceEnrollmentRequest = {},
        options: CommerceOperationOptions = {},
    ) {
        this.assertAvailable();
        return this.acceptAndSynchronize(await this.repository.begin(request, options), "started");
    }
    async resume(id?: string, options: CommerceOperationOptions = {}) {
        this.assertAvailable();
        const enrollment = await this.repository.get(id, options);
        if (!enrollment && id === undefined) return await this.begin({}, options);
        if (!enrollment)
            throw new CommerceError(
                "enrollment-not-found",
                "The enrollment journey was not found.",
            );
        return this.acceptAndSynchronize(enrollment, "resumed");
    }
    async restoreIfAvailable(options: CommerceOperationOptions = {}) {
        this.assertAvailable();
        if (this.value) return this.value;
        if (this.restoreInFlight) return this.restoreInFlight;

        this.restoreInFlight = this.restoreExisting(options);
        try {
            return await this.restoreInFlight;
        } finally {
            this.restoreInFlight = undefined;
        }
    }
    async selectKit(kitId: string, options: CommerceOperationOptions = {}) {
        return this.acceptAndSynchronize(
            await this.repository.selectKit(this.require(), kitId, options),
            "kit-selected",
        );
    }
    async addProduct(
        request: Omit<AddCommerceEnrollmentProductRequest, "enrollmentId" | "expectedVersion">,
        options: CommerceOperationOptions = {},
    ) {
        return this.acceptAndSynchronize(
            await this.repository.addProduct(this.require(), request, options),
            "product-added",
        );
    }
    async completeStage(outcome: string, options: CommerceOperationOptions = {}) {
        return this.acceptAndSynchronize(
            await this.repository.completeStage(this.require(), outcome, options),
            "stage-completed",
        );
    }
    async returnToShopping(options: CommerceOperationOptions = {}) {
        this.assertAvailable();
        return this.acceptAndSynchronize(
            await this.repository.returnToShopping(options),
            "returned-to-shopping",
        );
    }
    async abandon(options: CommerceOperationOptions = {}) {
        const enrollment = this.require();
        await this.repository.abandon(enrollment, options);
        this.value = null;
        this.listeners.emit(Object.freeze({ reason: "abandoned", enrollment: null }));
        await this.synchronizeCart(null);
    }
    onChanged(listener: CommerceEnrollmentChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }
    dispose() {
        this.value = null;
        this.restoreInFlight = undefined;
        this.listeners.release();
    }
    invalidate(): void {
        this.value = null;
        this.restoreInFlight = undefined;
    }
    clearAfterCustomerLogout(): void {
        this.value = null;
        this.restoreInFlight = undefined;
        this.listeners.emit(Object.freeze({ reason: "customer-logged-out", enrollment: null }));
    }
    async refreshAfterCartChanged(cart: CommerceCart, options: CommerceOperationOptions = {}) {
        if (!this.value) return;
        const enrollment = await this.repository.get(undefined, options);
        if (!enrollment) {
            this.clearAfterCartChanged();
            return;
        }

        const metadata = new Map(
            enrollment.cart.lines.map((value) => [value.line.id, value] as const),
        );
        if (cart.lines.some((line) => !metadata.has(line.id))) {
            this.clearAfterCartChanged();
            return;
        }
        const lines = cart.lines.map((line) => ({ ...metadata.get(line.id)!, line }));
        this.accept(
            {
                ...enrollment,
                cart: {
                    id: cart.id,
                    version: cart.version,
                    currencyCode: cart.currencyCode,
                    lines,
                    totals: cart.totals,
                },
                updatedAt: cart.updatedAt,
            },
            "cart-changed",
        );
    }
    private require() {
        this.assertAvailable();
        if (!this.value)
            throw new CommerceError(
                "enrollment-not-found",
                "No active enrollment journey is available.",
            );
        return this.value;
    }
    private accept(
        enrollment: CommerceEnrollmentSession,
        reason: CommerceEnrollmentChangedEvent["reason"],
    ) {
        this.value = Object.freeze(enrollment);
        this.listeners.emit(Object.freeze({ reason, enrollment: this.value }));
        return this.value;
    }

    private async acceptAndSynchronize(
        enrollment: CommerceEnrollmentSession,
        reason: CommerceEnrollmentChangedEvent["reason"],
    ) {
        const accepted = this.accept(enrollment, reason);
        await this.synchronizeCart(accepted);
        return accepted;
    }

    private async restoreExisting(
        options: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession | null> {
        const enrollment = await this.repository.get(undefined, options);
        if (!enrollment) return null;
        return this.acceptAndSynchronize(enrollment, "resumed");
    }

    private clearAfterCartChanged(): void {
        this.value = null;
        this.listeners.emit(Object.freeze({ reason: "abandoned", enrollment: null }));
    }
}
